import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import ViewQuiltRoundedIcon from '@mui/icons-material/ViewQuiltRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import MaterialTable from 'material-table';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { api } from 'common';
import { DEFAULT_WEBSITE_CONTENT, getWebsiteContent } from '../common/websiteUtils';
import { hasPermission } from '../common/permissionUtils';
import TableIcons from '../components/TableIcons';

const WEBSITE_TEMPLATES = [
  {
    key: 'classic',
    label: 'Classic',
    description: 'Clean, neutral layout with a dark hero.',
    gradient: 'linear-gradient(135deg, rgba(11,11,11,0.96), rgba(30,41,59,0.86))',
  },
  {
    key: 'aura',
    label: 'Aura',
    description: 'Bold hero, spacious sections, premium feel.',
    gradient: 'linear-gradient(135deg, rgba(15,23,42,0.95), rgba(30,41,59,0.85))',
  },
  {
    key: 'metro',
    label: 'Metro',
    description: 'City-forward energy with high contrast highlights.',
    gradient: 'linear-gradient(135deg, rgba(2,132,199,0.95), rgba(30,64,175,0.85))',
  },
  {
    key: 'minimal',
    label: 'Minimal',
    description: 'Clean white canvas with subtle accents.',
    gradient: 'linear-gradient(135deg, rgba(248,250,252,0.95), rgba(226,232,240,0.9))',
  },
  {
    key: 'sunrise',
    label: 'Sunrise',
    description: 'Warm gradients with a friendly, approachable tone.',
    gradient: 'linear-gradient(135deg, rgba(251,191,36,0.9), rgba(248,113,113,0.8))',
  },
];

const normalizeLangList = (langlist) => {
  if (Array.isArray(langlist)) return langlist.filter(Boolean);
  if (langlist && typeof langlist === 'object') return Object.values(langlist).filter(Boolean);
  return [];
};

const STORAGE_VERSION = 1;
const buildStorageKey = (uid) => `plukk.website_studio.draft.v${STORAGE_VERSION}:${uid || 'anon'}`;

const isPlainObject = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));

const deepEqual = (a, b) => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
};

const buildOverrides = (baseValue, currentValue) => {
  if (deepEqual(baseValue, currentValue)) return undefined;

  if (Array.isArray(currentValue)) return currentValue;
  if (isPlainObject(currentValue)) {
    const out = {};
    const baseObj = isPlainObject(baseValue) ? baseValue : {};
    const keys = new Set([...Object.keys(baseObj), ...Object.keys(currentValue)]);
    keys.forEach((key) => {
      if (key === 'translations') return;
      const child = buildOverrides(baseObj[key], currentValue[key]);
      if (child !== undefined) out[key] = child;
    });
    return Object.keys(out).length ? out : undefined;
  }
  return currentValue;
};

const countLeaves = (value) => {
  if (value === undefined) return 0;
  if (Array.isArray(value)) return 1;
  if (isPlainObject(value)) return Object.values(value).reduce((sum, child) => sum + countLeaves(child), 0);
  return 1;
};

const setAtPath = (obj, path, value) => {
  if (!path || !path.length) return value;
  const [head, ...tail] = path;
  const current = obj && typeof obj === 'object' ? obj : {};
  const next = Array.isArray(current) ? [...current] : { ...current };
  next[head] = setAtPath(current[head], tail, value);
  return next;
};

const collectDiffRows = (baseValue, currentValue, prefix = []) => {
  if (deepEqual(baseValue, currentValue)) return [];
  if (Array.isArray(currentValue) || Array.isArray(baseValue)) {
    return [{
      path: prefix,
      base: baseValue,
      current: currentValue,
      kind: 'array',
    }];
  }
  if (isPlainObject(currentValue) && isPlainObject(baseValue)) {
    const keys = new Set([...Object.keys(baseValue), ...Object.keys(currentValue)]);
    const rows = [];
    keys.forEach((key) => {
      if (key === 'translations') return;
      rows.push(...collectDiffRows(baseValue[key], currentValue[key], [...prefix, key]));
    });
    return rows;
  }
  return [{
    path: prefix,
    base: baseValue,
    current: currentValue,
    kind: 'value',
  }];
};

const sectionToggle = (label, value, onChange) => (
  <FormControlLabel
    control={<Switch checked={value} onChange={(e) => onChange(e.target.checked)} />}
    label={label}
  />
);

export default function WebsiteStudio() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settingsdata = useSelector((state) => state.settingsdata);
  const auth = useSelector((state) => state.auth);
  const languagedata = useSelector((state) => state.languagedata);
  const { editSettings } = api;

  const settingsValue = useMemo(() => settingsdata.settings || {}, [settingsdata.settings]);
  const canUpdateSettings = hasPermission(auth.profile, settingsValue, 'settings', 'update');

  const [editLocale, setEditLocale] = useState('base');
  const [draftByLocale, setDraftByLocale] = useState({});
  const [dirtyByLocale, setDirtyByLocale] = useState({});
  const [viewMode, setViewMode] = useState('edit'); // edit | diff
  const [autosavedAt, setAutosavedAt] = useState(null);
  const [restoredAt, setRestoredAt] = useState(null);
  const [navHomepageOpen, setNavHomepageOpen] = useState(true);
  const [expanded, setExpanded] = useState({
    template: true,
    branding: true,
    navigation: false,
    order: true,
    hero: true,
    map: false,
    howItWorks: false,
    valueProps: false,
    driverBusiness: false,
    download: false,
    diff: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const sectionRefs = useRef({});
  const skipAutosaveRef = useRef(true);
  const storageKey = useMemo(() => buildStorageKey(auth?.profile?.uid), [auth?.profile?.uid]);

  const languages = useMemo(() => {
    const list = normalizeLangList(languagedata?.langlist);
    return [...list].sort((a, b) => String(a.langName || '').localeCompare(String(b.langName || '')));
  }, [languagedata?.langlist]);

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        skipAutosaveRef.current = false;
        return;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== STORAGE_VERSION || !parsed.drafts) {
        skipAutosaveRef.current = false;
        return;
      }
      setDraftByLocale(parsed.drafts || {});
      setDirtyByLocale(parsed.dirtyByLocale || {});
      if (parsed.editLocale) setEditLocale(parsed.editLocale);
      setRestoredAt(parsed.updatedAt || Date.now());
    } catch (e) {
      // Ignore corrupted local drafts.
    } finally {
      skipAutosaveRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    if (!settingsValue) return;
    const locale = editLocale === 'base' ? null : editLocale;
    setDraftByLocale((prev) => {
      const next = { ...prev };
      if (!dirtyByLocale.base) {
        next.base = getWebsiteContent(settingsValue, null);
      }
      if (!dirtyByLocale[editLocale]) {
        next[editLocale] = getWebsiteContent(settingsValue, locale);
      } else if (!next[editLocale]) {
        // Defensive: if we somehow have a dirty flag but no draft yet, initialize it.
        next[editLocale] = getWebsiteContent(settingsValue, locale);
      }
      return next;
    });
  }, [settingsValue, editLocale, dirtyByLocale]);

  useEffect(() => {
    if (!settingsdata.loading && isSaving) {
      setIsSaving(false);
      setDirtyByLocale({});
      if (typeof window !== 'undefined' && storageKey) {
        try {
          window.localStorage.removeItem(storageKey);
        } catch (e) {
          // ignore
        }
      }
    }
  }, [settingsdata.loading, isSaving, storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!storageKey) return undefined;
    if (skipAutosaveRef.current) return undefined;
    const timer = window.setTimeout(() => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify({
          version: STORAGE_VERSION,
          updatedAt: Date.now(),
          editLocale,
          dirtyByLocale,
          drafts: draftByLocale,
        }));
        setAutosavedAt(Date.now());
      } catch (e) {
        // Ignore quota errors.
      }
    }, 800);
    return () => window.clearTimeout(timer);
  }, [storageKey, draftByLocale, dirtyByLocale, editLocale]);

  const draft = draftByLocale[editLocale] || DEFAULT_WEBSITE_CONTENT;
  const baseDraft = draftByLocale.base || getWebsiteContent(settingsValue, null) || DEFAULT_WEBSITE_CONTENT;
  const isBaseLocale = editLocale === 'base';
  const isDiffMode = viewMode === 'diff' && !isBaseLocale;
  const currentOverrides = isDiffMode ? (buildOverrides(baseDraft, draft) || {}) : {};
  const overridesCount = isDiffMode ? countLeaves(currentOverrides) : 0;
  const diffRows = useMemo(() => {
    if (!isDiffMode) return [];
    return collectDiffRows(baseDraft, draft);
  }, [isDiffMode, baseDraft, draft]);

  useEffect(() => {
    if (isBaseLocale && viewMode !== 'edit') {
      setViewMode('edit');
    }
  }, [isBaseLocale, viewMode]);

  const updateDraft = (updater) => {
    setDirtyByLocale((prev) => ({ ...prev, [editLocale]: true }));
    setDraftByLocale((prev) => {
      const current = prev[editLocale] || DEFAULT_WEBSITE_CONTENT;
      const next = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      return { ...prev, [editLocale]: next };
    });
  };

  const updateSection = (section, field, value) => {
    updateDraft((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value,
      },
    }));
  };

  const updateNested = (section, nested, field, value) => {
    updateDraft((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [nested]: {
          ...(prev[section]?.[nested] || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateNavigation = (field, value) => {
    updateDraft((prev) => ({
      ...prev,
      navigation: {
        ...(prev.navigation || {}),
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    if (!canUpdateSettings) return;
    if (!settingsdata.settings) return;
    setIsSaving(true);
    const existingContent = settingsdata.settings.websiteContent || {};
    const { translations: existingTranslations = {}, ...existingBase } = existingContent;
    const updatedTranslations = { ...existingTranslations };

    const strip = (content) => {
      const { translations, ...cleanContent } = content || {};
      return cleanContent || {};
    };

    const baseContent = strip(draftByLocale.base || existingBase || DEFAULT_WEBSITE_CONTENT);

    Object.entries(draftByLocale).forEach(([locale, content]) => {
      if (!content) return;
      if (locale === 'base') return;
      const full = strip(content);
      const overrides = buildOverrides(baseContent, full);
      if (!overrides || !Object.keys(overrides).length) {
        delete updatedTranslations[locale];
        return;
      }
      // These are global, not per-language.
      delete overrides.template;
      delete overrides.sectionOrder;
      delete overrides.enabledSections;
      if (!Object.keys(overrides).length) {
        delete updatedTranslations[locale];
        return;
      }
      updatedTranslations[locale] = overrides;
    });

    dispatch(editSettings({
      ...settingsdata.settings,
      websiteContent: {
        ...baseContent,
        translations: updatedTranslations,
      },
    }));
  };

  const applyDemo = () => {
    if (!canUpdateSettings) return;
    if (editLocale !== 'base') return;
    setDirtyByLocale((prev) => ({ ...(prev || {}), base: true }));
    setDraftByLocale((prev) => ({ ...prev, base: DEFAULT_WEBSITE_CONTENT }));
  };

  const tableOptions = {
    search: false,
    paging: false,
    toolbar: false,
    actionsColumnIndex: -1,
    headerStyle: { fontWeight: 700 },
  };

  const canEditGlobal = canUpdateSettings && isBaseLocale;
  const hasUnsaved = Object.values(dirtyByLocale || {}).some(Boolean);

  const homepageSections = useMemo(() => ([
    { id: 'order', label: t('section_order') || 'Section order', icon: ViewQuiltRoundedIcon },
    { id: 'hero', label: t('hero_section') || 'Hero section', key: 'hero', icon: HomeRoundedIcon },
    { id: 'map', label: t('map_section') || 'Map section', key: 'map', icon: HomeRoundedIcon },
    { id: 'howItWorks', label: t('how_it_works_label') || 'How it works', key: 'howItWorks', icon: HomeRoundedIcon },
    { id: 'valueProps', label: t('value_props_label') || 'Value propositions', key: 'valueProps', icon: HomeRoundedIcon },
    { id: 'driverBusiness', label: t('driver_business_section') || 'Driver & business section', key: 'driverBusiness', icon: HomeRoundedIcon },
    { id: 'download', label: t('download_section') || 'Download section', key: 'download', icon: HomeRoundedIcon },
  ]), [t]);

  const orderedHomepage = useMemo(() => {
    const baseOrder = Array.isArray(baseDraft.sectionOrder) && baseDraft.sectionOrder.length
      ? baseDraft.sectionOrder
      : DEFAULT_WEBSITE_CONTENT.sectionOrder;
    const byKey = new Map(homepageSections.filter((s) => s.key).map((s) => [s.key, s]));
    const seen = new Set();
    const ordered = [];
    baseOrder.forEach((key) => {
      if (seen.has(key)) return;
      const item = byKey.get(key);
      if (!item) return;
      seen.add(key);
      ordered.push(item);
    });
    homepageSections.forEach((item) => {
      if (!item.key) return;
      if (seen.has(item.key)) return;
      seen.add(item.key);
      ordered.push(item);
    });
    return [
      homepageSections.find((s) => s.id === 'order'),
      ...ordered,
    ].filter(Boolean);
  }, [homepageSections, baseDraft.sectionOrder]);

  const handleScrollTo = (id) => {
    const node = sectionRefs.current[id];
    if (node?.scrollIntoView) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openAndScroll = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: true }));
    // Let Accordion render before scroll.
    window.setTimeout(() => handleScrollTo(id), 60);
  };

  const updateBaseDraft = (updater) => {
    setDirtyByLocale((prev) => ({ ...(prev || {}), base: true }));
    setDraftByLocale((prev) => {
      const current = prev.base || getWebsiteContent(settingsValue, null) || DEFAULT_WEBSITE_CONTENT;
      const next = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      return { ...prev, base: next };
    });
  };

  const updateGlobalSection = (section, field, value) => {
    updateBaseDraft((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value,
      },
    }));
  };

  const moveSection = (key, direction) => {
    updateBaseDraft((prev) => {
      const order = Array.isArray(prev.sectionOrder) && prev.sectionOrder.length
        ? [...prev.sectionOrder]
        : [...DEFAULT_WEBSITE_CONTENT.sectionOrder];
      const idx = order.indexOf(key);
      if (idx === -1) return prev;
      const nextIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (nextIdx < 0 || nextIdx >= order.length) return prev;
      const next = [...order];
      const tmp = next[idx];
      next[idx] = next[nextIdx];
      next[nextIdx] = tmp;
      return { ...prev, sectionOrder: next };
    });
  };

  const discardLocalDrafts = () => {
    if (typeof window !== 'undefined' && storageKey) {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (e) {
        // ignore
      }
    }
    setDraftByLocale({});
    setDirtyByLocale({});
    setRestoredAt(null);
    setAutosavedAt(null);
  };

  const resetLocaleToBase = () => {
    if (isBaseLocale) return;
    setDirtyByLocale((prev) => ({ ...(prev || {}), [editLocale]: true }));
    setDraftByLocale((prev) => ({ ...prev, [editLocale]: baseDraft }));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 14px 30px rgba(15,23,42,0.08)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('website_studio') || 'Website Studio'}</Typography>
              <Typography sx={{ color: '#64748b', mt: 0.5 }}>
                {t('website_studio_subtitle') || 'Update the landing experience, branding, and navigation.'}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                {hasUnsaved ? (
                  <Chip
                    label={t('unsaved_changes') || 'Unsaved changes'}
                    sx={{ bgcolor: 'rgba(245,158,11,0.14)', color: '#92400e', fontWeight: 700 }}
                  />
                ) : (
                  <Chip
                    label={t('all_changes_saved') || 'All changes saved'}
                    sx={{ bgcolor: 'rgba(34,197,94,0.12)', color: '#166534', fontWeight: 700 }}
                  />
                )}
                {!isBaseLocale && (
                  <Chip
                    label={(t('editing_language') || 'Editing') + `: ${editLocale.toUpperCase()}`}
                    sx={{ bgcolor: '#e2e8f0', fontWeight: 700 }}
                  />
                )}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<VisibilityRoundedIcon />}
                onClick={() => window.open('/', '_blank', 'noopener,noreferrer')}
              >
                {t('preview_site') || 'Preview'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<AutoFixHighRoundedIcon />}
                onClick={applyDemo}
                disabled={!canUpdateSettings || !isBaseLocale}
              >
                {t('load_defaults') || 'Load defaults'}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                onClick={handleSave}
                disabled={!canUpdateSettings || isSaving}
              >
                {isSaving ? t('saving') || 'Saving' : t('save_changes') || 'Save changes'}
              </Button>
            </Stack>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('content_language') || 'Content language'}</Typography>
            <Tabs
              value={editLocale}
              onChange={(e, value) => setEditLocale(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="base" label={t('default') || 'Default'} />
              {languages.map((lang) => {
                const code = lang?.langLocale || lang?.id;
                if (!code) return null;
                return <Tab key={code} value={code} label={lang?.langName || code.toUpperCase()} />;
              })}
            </Tabs>
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ToggleButtonGroup
                size="small"
                value={viewMode}
                exclusive
                onChange={(e, next) => {
                  if (!next) return;
                  setViewMode(next);
                }}
                disabled={isBaseLocale}
              >
                <ToggleButton value="edit">{t('edit') || 'Edit'}</ToggleButton>
                <ToggleButton value="diff">
                  {t('diff') || 'Diff'}
                  {!isBaseLocale && (
                    <Chip
                      size="small"
                      label={overridesCount}
                      sx={{ ml: 1, bgcolor: 'rgba(15,23,42,0.06)', fontWeight: 700 }}
                    />
                  )}
                </ToggleButton>
              </ToggleButtonGroup>
              {!isBaseLocale && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={resetLocaleToBase}
                  disabled={!canUpdateSettings}
                >
                  {t('reset_to_default') || 'Reset to default'}
                </Button>
              )}
            </Stack>
            {!isBaseLocale && (
              <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                {t('global_base_only_hint') || 'Template, section order, and section visibility are global (edit in Default).'}
              </Typography>
            )}
          </Stack>

          {(restoredAt || autosavedAt) && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity="info"
                action={(
                  <Button color="inherit" size="small" onClick={discardLocalDrafts}>
                    {t('discard_drafts') || 'Discard drafts'}
                  </Button>
                )}
              >
                <Stack spacing={0.5}>
                  {restoredAt && (
                    <Typography variant="body2">
                      {(t('draft_restored') || 'Draft restored from your browser') + ` (${new Date(restoredAt).toLocaleString()}).`}
                    </Typography>
                  )}
                  {autosavedAt && (
                    <Typography variant="body2">
                      {(t('draft_autosaved') || 'Autosaved') + ` ${new Date(autosavedAt).toLocaleTimeString()}.`}
                    </Typography>
                  )}
                </Stack>
              </Alert>
            </Box>
          )}

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3}>
              <Box sx={{ position: { md: 'sticky' }, top: 16 }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    borderColor: 'rgba(148,163,184,0.35)',
                    bgcolor: '#f8fafc',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography sx={{ fontWeight: 800, px: 1, pt: 0.5, pb: 1 }}>
                      {t('sections') || 'Sections'}
                    </Typography>
                    <List dense disablePadding>
                      <ListItemButton selected={expanded.template} onClick={() => openAndScroll('template')}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PaletteRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t('template_gallery') || 'Template gallery'} secondary={t('global') || 'Global'} />
                      </ListItemButton>
                      <ListItemButton selected={expanded.branding} onClick={() => openAndScroll('branding')}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PaletteRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t('branding_seo') || 'Branding & SEO'} />
                      </ListItemButton>
                      <ListItemButton selected={expanded.navigation} onClick={() => openAndScroll('navigation')}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <LinkRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t('navigation_footer') || 'Navigation & Footer'} />
                      </ListItemButton>

                      <Divider sx={{ my: 1 }} />

                      <ListItemButton onClick={() => setNavHomepageOpen((prev) => !prev)}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <HomeRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={t('homepage') || 'Homepage'}
                          secondary={t('reorder_sections') || 'Reorder & toggle sections'}
                        />
                        <ExpandMoreIcon
                          sx={{
                            transform: navHomepageOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                          }}
                        />
                      </ListItemButton>
                      <Collapse in={navHomepageOpen} timeout="auto" unmountOnExit>
                        <List dense disablePadding sx={{ pl: 1 }}>
                          {orderedHomepage.map((item) => {
                            const enabled = item.key ? (baseDraft.enabledSections?.[item.key] !== false) : true;
                            const order = Array.isArray(baseDraft.sectionOrder) && baseDraft.sectionOrder.length
                              ? baseDraft.sectionOrder
                              : DEFAULT_WEBSITE_CONTENT.sectionOrder;
                            const idx = item.key ? order.indexOf(item.key) : -1;
                            const canMoveUp = canEditGlobal && item.key && idx > 0;
                            const canMoveDown = canEditGlobal && item.key && idx !== -1 && idx < order.length - 1;
                            const Icon = item.icon || HomeRoundedIcon;

                            return (
                              <ListItemButton
                                key={item.id}
                                selected={Boolean(expanded[item.id])}
                                onClick={() => openAndScroll(item.id)}
                                sx={{ borderRadius: 1.5, pr: 1 }}
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <Icon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.label}
                                  secondary={!enabled ? (t('hidden') || 'Hidden') : undefined}
                                />
                                {item.key && (
                                  <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                                    <Tooltip title={t('move_up') || 'Move up'}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={!canMoveUp}
                                          onClick={() => moveSection(item.key, 'up')}
                                        >
                                          <KeyboardArrowUpRoundedIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title={t('move_down') || 'Move down'}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={!canMoveDown}
                                          onClick={() => moveSection(item.key, 'down')}
                                        >
                                          <KeyboardArrowDownRoundedIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                )}
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    </List>
                  </CardContent>
                </Card>

                {(restoredAt || autosavedAt) && (
                  <Card
                    variant="outlined"
                    sx={{
                      mt: 2,
                      borderRadius: 2.5,
                      borderColor: 'rgba(148,163,184,0.35)',
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography sx={{ fontWeight: 800, mb: 1 }}>
                        {t('drafts') || 'Drafts'}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                        {t('drafts_saved_locally') || 'Drafts are autosaved locally in your browser.'}
                      </Typography>
                      {restoredAt && (
                        <Typography sx={{ color: '#0f172a', fontSize: 13, mt: 1 }}>
                          {(t('draft_restored') || 'Restored') + `: ${new Date(restoredAt).toLocaleString()}`}
                        </Typography>
                      )}
                      {autosavedAt && (
                        <Typography sx={{ color: '#0f172a', fontSize: 13, mt: 0.5 }}>
                          {(t('draft_autosaved') || 'Autosaved') + `: ${new Date(autosavedAt).toLocaleString()}`}
                        </Typography>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1.5 }}
                        startIcon={<RestartAltRoundedIcon />}
                        onClick={discardLocalDrafts}
                      >
                        {t('discard_drafts') || 'Discard drafts'}
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              {isDiffMode && (
                <Card
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderRadius: 2.5,
                    borderColor: 'rgba(148,163,184,0.35)',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      alignItems={{ md: 'center' }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          {t('diff_view_title') || 'Differences from default'}
                        </Typography>
                        <Typography sx={{ color: '#64748b', mt: 0.5 }}>
                          {(t('diff_view_subtitle') || 'Review what is overridden in this language') + ` (${editLocale.toUpperCase()}).`}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          label={(t('overrides') || 'Overrides') + `: ${overridesCount}`}
                          sx={{ bgcolor: 'rgba(15,23,42,0.06)', fontWeight: 800 }}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<RestartAltRoundedIcon />}
                          onClick={resetLocaleToBase}
                          disabled={!canUpdateSettings}
                        >
                          {t('reset_locale') || 'Reset locale'}
                        </Button>
                      </Stack>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    {diffRows.length === 0 ? (
                      <Typography sx={{ color: '#64748b' }}>
                        {t('no_overrides') || 'No overrides yet. This language matches the default.'}
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {diffRows
                          .filter((row) => row?.path?.length && !['template', 'sectionOrder', 'enabledSections'].includes(row.path[0]))
                          .slice(0, 80)
                          .map((row, idx) => {
                            const pathStr = row.path.join('.');
                            const sectionRoot = row.path[0];
                            const sectionId = sectionRoot === 'seo' ? 'branding'
                              : sectionRoot === 'driver' || sectionRoot === 'business' ? 'driverBusiness'
                              : sectionRoot;
                            const preview = (value) => {
                              if (value === undefined) return '';
                              if (value === null) return 'null';
                              if (typeof value === 'string') return value;
                              if (typeof value === 'number' || typeof value === 'boolean') return String(value);
                              try { return JSON.stringify(value); } catch (e) { return String(value); }
                            };
                            const truncate = (value) => (value && value.length > 160 ? `${value.slice(0, 160)}...` : value);
                            return (
                              <Box
                                key={`${pathStr}-${idx}`}
                                sx={{
                                  border: '1px solid rgba(148,163,184,0.28)',
                                  borderRadius: 2,
                                  p: 1.5,
                                  bgcolor: 'rgba(248,250,252,0.7)',
                                }}
                              >
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{pathStr}</Typography>
                                    <Typography sx={{ color: '#64748b', fontSize: 12 }}>
                                      {(t('section') || 'Section') + `: ${String(sectionId)}`}
                                    </Typography>
                                  </Box>
                                  <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<VisibilityRoundedIcon />}
                                      onClick={() => openAndScroll(sectionId)}
                                    >
                                      {t('open') || 'Open'}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<RestartAltRoundedIcon />}
                                      onClick={() => updateDraft((prev) => setAtPath(prev, row.path, row.base))}
                                    >
                                      {t('reset') || 'Reset'}
                                    </Button>
                                  </Stack>
                                </Stack>
                                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                  <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
                                      {t('default') || 'Default'}
                                    </Typography>
                                    <Box
                                      component="pre"
                                      sx={{
                                        m: 0,
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: '#ffffff',
                                        border: '1px solid rgba(148,163,184,0.25)',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontSize: 12,
                                      }}
                                    >
                                      {truncate(preview(row.base))}
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
                                      {t('current') || 'Current'}
                                    </Typography>
                                    <Box
                                      component="pre"
                                      sx={{
                                        m: 0,
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: '#ffffff',
                                        border: '1px solid rgba(148,163,184,0.25)',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontSize: 12,
                                      }}
                                    >
                                      {truncate(preview(row.current))}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            );
                          })}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              )}
          <Accordion
            expanded={expanded.template}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, template: value }))}
            ref={(node) => { sectionRefs.current.template = node; }}
            id="template"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('template_gallery') || 'Template gallery'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ color: '#64748b', mb: 3 }}>
                {t('template_gallery_subtitle') || 'Pick a layout style for the landing experience.'}
              </Typography>
              <Grid container spacing={2}>
                {WEBSITE_TEMPLATES.map((template) => (
                  <Grid item xs={12} md={6} key={template.key}>
                    <Card
                      variant="outlined"
	                      sx={{
	                        p: 2,
	                        borderRadius: 2,
	                        borderColor: baseDraft.template === template.key ? '#0f172a' : 'rgba(148,163,184,0.4)',
	                        boxShadow: baseDraft.template === template.key ? '0 18px 40px rgba(15,23,42,0.18)' : 'none',
	                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
	                        '&:hover': { transform: 'translateY(-2px)' },
	                      }}
	                    >
                      <Box sx={{ height: 120, borderRadius: 2, background: template.gradient, mb: 2 }} />
                      <Typography sx={{ fontWeight: 700 }}>{template.label}</Typography>
                      <Typography sx={{ color: '#64748b', fontSize: 13, mb: 2 }}>
                        {template.description}
                      </Typography>
	                      <Tooltip title={canEditGlobal ? '' : (t('global_base_only') || 'Edit global settings from the Default language.')}>
	                        <span>
	                          <Button
	                            variant={baseDraft.template === template.key ? 'contained' : 'outlined'}
	                            onClick={() => updateBaseDraft({ template: template.key })}
	                            disabled={!canEditGlobal}
	                          >
	                            {baseDraft.template === template.key ? t('selected') || 'Selected' : t('apply_template') || 'Apply template'}
	                          </Button>
	                        </span>
	                      </Tooltip>
	                    </Card>
	                  </Grid>
	                ))}
	              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.branding}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, branding: value }))}
            ref={(node) => { sectionRefs.current.branding = node; }}
            id="branding"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('branding_seo') || 'Branding & SEO'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('brand_name') || 'Brand name'}
                    fullWidth
                    value={draft.branding?.brandName || ''}
                    onChange={(e) => updateSection('branding', 'brandName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('brand_logo') || 'Logo URL'}
                    fullWidth
                    value={draft.branding?.logoUrl || ''}
                    onChange={(e) => updateSection('branding', 'logoUrl', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('favicon_url') || 'Favicon URL'}
                    fullWidth
                    value={draft.branding?.faviconUrl || ''}
                    onChange={(e) => updateSection('branding', 'faviconUrl', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('seo_title') || 'SEO title'}
                    fullWidth
                    value={draft.seo?.title || ''}
                    onChange={(e) => updateSection('seo', 'title', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('og_image') || 'OpenGraph image'}
                    fullWidth
                    value={draft.seo?.ogImage || ''}
                    onChange={(e) => updateSection('seo', 'ogImage', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('seo_description') || 'SEO description'}
                    fullWidth
                    multiline
                    minRows={3}
                    value={draft.seo?.description || ''}
                    onChange={(e) => updateSection('seo', 'description', e.target.value)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.navigation}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, navigation: value }))}
            ref={(node) => { sectionRefs.current.navigation = node; }}
            id="navigation"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('navigation_footer') || 'Navigation & Footer'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('nav_ride_links') || 'Ride menu links'}</Typography>
                  <MaterialTable
                    icons={TableIcons}
                    columns={[
                      { title: t('label') || 'Label', field: 'label' },
                      { title: t('path') || 'Path', field: 'path' },
                      { title: t('visible') || 'Visible', field: 'visible', type: 'boolean' },
                    ]}
                    data={draft.navigation?.rideLinks || []}
                    options={tableOptions}
                    editable={canUpdateSettings ? {
                      onRowAdd: (newData) => new Promise((resolve) => {
                        const next = [...(draft.navigation?.rideLinks || []), newData];
                        updateNavigation('rideLinks', next);
                        resolve();
                      }),
                      onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.rideLinks || []).map((item) => item === oldData ? newData : item);
                        updateNavigation('rideLinks', next);
                        resolve();
                      }),
                      onRowDelete: (oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.rideLinks || []).filter((item) => item !== oldData);
                        updateNavigation('rideLinks', next);
                        resolve();
                      }),
                    } : {}}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('nav_drive_links') || 'Drive menu links'}</Typography>
                  <MaterialTable
                    icons={TableIcons}
                    columns={[
                      { title: t('label') || 'Label', field: 'label' },
                      { title: t('path') || 'Path', field: 'path' },
                      { title: t('visible') || 'Visible', field: 'visible', type: 'boolean' },
                    ]}
                    data={draft.navigation?.driveLinks || []}
                    options={tableOptions}
                    editable={canUpdateSettings ? {
                      onRowAdd: (newData) => new Promise((resolve) => {
                        const next = [...(draft.navigation?.driveLinks || []), newData];
                        updateNavigation('driveLinks', next);
                        resolve();
                      }),
                      onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.driveLinks || []).map((item) => item === oldData ? newData : item);
                        updateNavigation('driveLinks', next);
                        resolve();
                      }),
                      onRowDelete: (oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.driveLinks || []).filter((item) => item !== oldData);
                        updateNavigation('driveLinks', next);
                        resolve();
                      }),
                    } : {}}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('nav_about_links') || 'About menu links'}</Typography>
                  <MaterialTable
                    icons={TableIcons}
                    columns={[
                      { title: t('label') || 'Label', field: 'label' },
                      { title: t('path') || 'Path', field: 'path' },
                      { title: t('visible') || 'Visible', field: 'visible', type: 'boolean' },
                    ]}
                    data={draft.navigation?.aboutLinks || []}
                    options={tableOptions}
                    editable={canUpdateSettings ? {
                      onRowAdd: (newData) => new Promise((resolve) => {
                        const next = [...(draft.navigation?.aboutLinks || []), newData];
                        updateNavigation('aboutLinks', next);
                        resolve();
                      }),
                      onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.aboutLinks || []).map((item) => item === oldData ? newData : item);
                        updateNavigation('aboutLinks', next);
                        resolve();
                      }),
                      onRowDelete: (oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.aboutLinks || []).filter((item) => item !== oldData);
                        updateNavigation('aboutLinks', next);
                        resolve();
                      }),
                    } : {}}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('footer_company_links') || 'Footer company links'}</Typography>
                  <MaterialTable
                    icons={TableIcons}
                    columns={[
                      { title: t('label') || 'Label', field: 'label' },
                      { title: t('path') || 'Path', field: 'path' },
                      { title: t('visible') || 'Visible', field: 'visible', type: 'boolean' },
                    ]}
                    data={draft.navigation?.footerCompany || []}
                    options={tableOptions}
                    editable={canUpdateSettings ? {
                      onRowAdd: (newData) => new Promise((resolve) => {
                        const next = [...(draft.navigation?.footerCompany || []), newData];
                        updateNavigation('footerCompany', next);
                        resolve();
                      }),
                      onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.footerCompany || []).map((item) => item === oldData ? newData : item);
                        updateNavigation('footerCompany', next);
                        resolve();
                      }),
                      onRowDelete: (oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.footerCompany || []).filter((item) => item !== oldData);
                        updateNavigation('footerCompany', next);
                        resolve();
                      }),
                    } : {}}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('footer_product_links') || 'Footer product links'}</Typography>
                  <MaterialTable
                    icons={TableIcons}
                    columns={[
                      { title: t('label') || 'Label', field: 'label' },
                      { title: t('path') || 'Path', field: 'path' },
                      { title: t('visible') || 'Visible', field: 'visible', type: 'boolean' },
                    ]}
                    data={draft.navigation?.footerProducts || []}
                    options={tableOptions}
                    editable={canUpdateSettings ? {
                      onRowAdd: (newData) => new Promise((resolve) => {
                        const next = [...(draft.navigation?.footerProducts || []), newData];
                        updateNavigation('footerProducts', next);
                        resolve();
                      }),
                      onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.footerProducts || []).map((item) => item === oldData ? newData : item);
                        updateNavigation('footerProducts', next);
                        resolve();
                      }),
                      onRowDelete: (oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.footerProducts || []).filter((item) => item !== oldData);
                        updateNavigation('footerProducts', next);
                        resolve();
                      }),
                    } : {}}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('footer_legal_links') || 'Footer legal links'}</Typography>
                  <MaterialTable
                    icons={TableIcons}
                    columns={[
                      { title: t('label') || 'Label', field: 'label' },
                      { title: t('path') || 'Path', field: 'path' },
                      { title: t('visible') || 'Visible', field: 'visible', type: 'boolean' },
                    ]}
                    data={draft.navigation?.footerLegal || []}
                    options={tableOptions}
                    editable={canUpdateSettings ? {
                      onRowAdd: (newData) => new Promise((resolve) => {
                        const next = [...(draft.navigation?.footerLegal || []), newData];
                        updateNavigation('footerLegal', next);
                        resolve();
                      }),
                      onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.footerLegal || []).map((item) => item === oldData ? newData : item);
                        updateNavigation('footerLegal', next);
                        resolve();
                      }),
                      onRowDelete: (oldData) => new Promise((resolve) => {
                        const next = (draft.navigation?.footerLegal || []).filter((item) => item !== oldData);
                        updateNavigation('footerLegal', next);
                        resolve();
                      }),
                    } : {}}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.order}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, order: value }))}
            ref={(node) => { sectionRefs.current.order = node; }}
            id="order"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('section_order') || 'Section order'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ color: '#64748b', mb: 1.5 }}>
                {t('section_order_help') || 'Use the arrows to reorder sections and toggle visibility.'}
              </Typography>
              <Stack spacing={1}>
                {orderedHomepage.filter((item) => item.key).map((item) => {
                  const enabled = baseDraft.enabledSections?.[item.key] !== false;
                  const order = Array.isArray(baseDraft.sectionOrder) && baseDraft.sectionOrder.length
                    ? baseDraft.sectionOrder
                    : DEFAULT_WEBSITE_CONTENT.sectionOrder;
                  const idx = order.indexOf(item.key);
                  const canMoveUp = canEditGlobal && idx > 0;
                  const canMoveDown = canEditGlobal && idx !== -1 && idx < order.length - 1;

                  return (
                    <Box
                      key={item.key}
                      sx={{
                        border: '1px solid rgba(148,163,184,0.28)',
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: '#ffffff',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ md: 'center' }}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 800 }}>{item.label}</Typography>
                          <Typography sx={{ color: '#64748b', fontSize: 12 }}>{item.key}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={enabled}
                                disabled={!canEditGlobal}
                                onChange={(e) => updateGlobalSection('enabledSections', item.key, e.target.checked)}
                              />
                            )}
                            label={enabled ? (t('enabled') || 'Enabled') : (t('hidden') || 'Hidden')}
                          />
                          <Tooltip title={t('move_up') || 'Move up'}>
                            <span>
                              <IconButton size="small" disabled={!canMoveUp} onClick={() => moveSection(item.key, 'up')}>
                                <KeyboardArrowUpRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('move_down') || 'Move down'}>
                            <span>
                              <IconButton size="small" disabled={!canMoveDown} onClick={() => moveSection(item.key, 'down')}>
                                <KeyboardArrowDownRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('open') || 'Open'}>
                            <IconButton size="small" onClick={() => openAndScroll(item.id)}>
                              <VisibilityRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.hero}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, hero: value }))}
            ref={(node) => { sectionRefs.current.hero = node; }}
            id="hero"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('hero_section') || 'Hero section'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={(
                  <Switch
                    checked={baseDraft.enabledSections?.hero !== false}
                    disabled={!canEditGlobal}
                    onChange={(e) => updateGlobalSection('enabledSections', 'hero', e.target.checked)}
                  />
                )}
                label={t('section_visible') || 'Section visible'}
              />
              <TextField
                label={t('hero_title_label') || 'Hero title'}
                fullWidth
                sx={{ mt: 2 }}
                value={draft.hero?.title || ''}
                onChange={(e) => updateSection('hero', 'title', e.target.value)}
              />
              <TextField
                label={t('hero_subtitle_label') || 'Hero subtitle'}
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 2 }}
                value={draft.hero?.subtitle || ''}
                onChange={(e) => updateSection('hero', 'subtitle', e.target.value)}
              />
              <TextField
                label={t('hero_image_label') || 'Hero image URL'}
                fullWidth
                sx={{ mt: 2 }}
                value={draft.hero?.image || ''}
                onChange={(e) => updateSection('hero', 'image', e.target.value)}
              />
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('primary_cta_label') || 'Primary CTA'}
                    fullWidth
                    value={draft.hero?.primaryCtaLabel || ''}
                    onChange={(e) => updateSection('hero', 'primaryCtaLabel', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('secondary_cta_label') || 'Secondary CTA'}
                    fullWidth
                    value={draft.hero?.secondaryCtaLabel || ''}
                    onChange={(e) => updateSection('hero', 'secondaryCtaLabel', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  {sectionToggle(
                    t('hero_driver_card') || 'Show driver promo card',
                    draft.hero?.showDriverCard !== false,
                    (value) => updateSection('hero', 'showDriverCard', value),
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('hero_driver_title') || 'Driver card title'}
                    fullWidth
                    value={draft.hero?.driverCardTitle || ''}
                    onChange={(e) => updateSection('hero', 'driverCardTitle', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('hero_driver_subtitle') || 'Driver card subtitle'}
                    fullWidth
                    value={draft.hero?.driverCardSubtitle || ''}
                    onChange={(e) => updateSection('hero', 'driverCardSubtitle', e.target.value)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.map}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, map: value }))}
            ref={(node) => { sectionRefs.current.map = node; }}
            id="map"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('map_section') || 'Map section'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={(
                  <Switch
                    checked={baseDraft.enabledSections?.map !== false}
                    disabled={!canEditGlobal}
                    onChange={(e) => updateGlobalSection('enabledSections', 'map', e.target.checked)}
                  />
                )}
                label={t('section_visible') || 'Section visible'}
              />
              <TextField
                label={t('map_title_label') || 'Map section title'}
                fullWidth
                sx={{ mt: 2 }}
                value={draft.map?.title || ''}
                onChange={(e) => updateSection('map', 'title', e.target.value)}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.howItWorks}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, howItWorks: value }))}
            ref={(node) => { sectionRefs.current.howItWorks = node; }}
            id="howItWorks"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('how_it_works_label') || 'How it works'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={(
                  <Switch
                    checked={baseDraft.enabledSections?.howItWorks !== false}
                    disabled={!canEditGlobal}
                    onChange={(e) => updateGlobalSection('enabledSections', 'howItWorks', e.target.checked)}
                  />
                )}
                label={t('section_visible') || 'Section visible'}
              />
              <TextField
                label={t('how_it_works_title_label') || 'Section title'}
                fullWidth
                sx={{ mt: 2 }}
                value={draft.howItWorks?.title || ''}
                onChange={(e) => updateSection('howItWorks', 'title', e.target.value)}
              />
              <Typography sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                {t('how_it_works_steps') || 'Steps'}
              </Typography>
              <MaterialTable
                icons={TableIcons}
                title={null}
                columns={[
                  { title: t('title') || 'Title', field: 'title' },
                  { title: t('description') || 'Description', field: 'description' },
                  { title: t('icon') || 'Icon', field: 'icon', tooltip: 'location | car | star' },
                ]}
                data={draft.howItWorks?.steps || []}
                options={tableOptions}
                editable={canUpdateSettings ? {
                  onRowAdd: (newData) => new Promise((resolve) => {
                    const next = [...(draft.howItWorks?.steps || []), newData];
                    updateSection('howItWorks', 'steps', next);
                    resolve();
                  }),
                  onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                    const next = (draft.howItWorks?.steps || []).map((item) => item === oldData ? newData : item);
                    updateSection('howItWorks', 'steps', next);
                    resolve();
                  }),
                  onRowDelete: (oldData) => new Promise((resolve) => {
                    const next = (draft.howItWorks?.steps || []).filter((item) => item !== oldData);
                    updateSection('howItWorks', 'steps', next);
                    resolve();
                  }),
                } : {}}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.valueProps}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, valueProps: value }))}
            ref={(node) => { sectionRefs.current.valueProps = node; }}
            id="valueProps"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('value_props_label') || 'Value propositions'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={(
                  <Switch
                    checked={baseDraft.enabledSections?.valueProps !== false}
                    disabled={!canEditGlobal}
                    onChange={(e) => updateGlobalSection('enabledSections', 'valueProps', e.target.checked)}
                  />
                )}
                label={t('section_visible') || 'Section visible'}
              />
              <Typography sx={{ fontWeight: 700, mt: 1, mb: 1 }}>{t('value_cards_label') || 'Value cards'}</Typography>
              <MaterialTable
                icons={TableIcons}
                title={null}
                columns={[
                  { title: t('title') || 'Title', field: 'title' },
                  { title: t('description') || 'Description', field: 'description' },
                  { title: t('link') || 'Link', field: 'link' },
                  { title: t('link_text') || 'Link text', field: 'linkText' },
                  { title: t('image') || 'Image URL', field: 'image' },
                ]}
                data={draft.valueProps?.cards || []}
                options={tableOptions}
                editable={canUpdateSettings ? {
                  onRowAdd: (newData) => new Promise((resolve) => {
                    const next = [...(draft.valueProps?.cards || []), newData];
                    updateSection('valueProps', 'cards', next);
                    resolve();
                  }),
                  onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                    const next = (draft.valueProps?.cards || []).map((item) => item === oldData ? newData : item);
                    updateSection('valueProps', 'cards', next);
                    resolve();
                  }),
                  onRowDelete: (oldData) => new Promise((resolve) => {
                    const next = (draft.valueProps?.cards || []).filter((item) => item !== oldData);
                    updateSection('valueProps', 'cards', next);
                    resolve();
                  }),
                } : {}}
              />

              <Typography sx={{ fontWeight: 800, mt: 3, mb: 1 }}>{t('safety_section_label') || 'Safety section'}</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('safety_title_label') || 'Safety title'}
                    fullWidth
                    value={draft.valueProps?.safety?.title || ''}
                    onChange={(e) => updateNested('valueProps', 'safety', 'title', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('safety_link_text_label') || 'Safety link text'}
                    fullWidth
                    value={draft.valueProps?.safety?.linkText || ''}
                    onChange={(e) => updateNested('valueProps', 'safety', 'linkText', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('safety_description_label') || 'Safety description'}
                    fullWidth
                    multiline
                    minRows={3}
                    value={draft.valueProps?.safety?.description || ''}
                    onChange={(e) => updateNested('valueProps', 'safety', 'description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('safety_link_label') || 'Safety link'}
                    fullWidth
                    value={draft.valueProps?.safety?.link || ''}
                    onChange={(e) => updateNested('valueProps', 'safety', 'link', e.target.value)}
                  />
                </Grid>
              </Grid>

              <Typography sx={{ fontWeight: 700, mb: 1 }}>{t('safety_features_label') || 'Safety features'}</Typography>
              <MaterialTable
                icons={TableIcons}
                title={null}
                columns={[
                  { title: t('title') || 'Title', field: 'title' },
                  { title: t('description') || 'Description', field: 'description' },
                ]}
                data={draft.valueProps?.safety?.features || []}
                options={tableOptions}
                editable={canUpdateSettings ? {
                  onRowAdd: (newData) => new Promise((resolve) => {
                    const next = [...(draft.valueProps?.safety?.features || []), newData];
                    updateNested('valueProps', 'safety', 'features', next);
                    resolve();
                  }),
                  onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                    const next = (draft.valueProps?.safety?.features || []).map((item) => item === oldData ? newData : item);
                    updateNested('valueProps', 'safety', 'features', next);
                    resolve();
                  }),
                  onRowDelete: (oldData) => new Promise((resolve) => {
                    const next = (draft.valueProps?.safety?.features || []).filter((item) => item !== oldData);
                    updateNested('valueProps', 'safety', 'features', next);
                    resolve();
                  }),
                } : {}}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.driverBusiness}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, driverBusiness: value }))}
            ref={(node) => { sectionRefs.current.driverBusiness = node; }}
            id="driverBusiness"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('driver_business_section') || 'Driver & business section'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={(
                  <Switch
                    checked={baseDraft.enabledSections?.driverBusiness !== false}
                    disabled={!canEditGlobal}
                    onChange={(e) => updateGlobalSection('enabledSections', 'driverBusiness', e.target.checked)}
                  />
                )}
                label={t('section_visible') || 'Section visible'}
              />
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontWeight: 800, mb: 1 }}>{t('driver_promo_label') || 'Driver promo'}</Typography>
                  <TextField
                    label={t('driver_title_label') || 'Driver title'}
                    fullWidth
                    value={draft.driver?.title || ''}
                    onChange={(e) => updateSection('driver', 'title', e.target.value)}
                  />
                  <TextField
                    label={t('driver_description_label') || 'Driver description'}
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mt: 2 }}
                    value={draft.driver?.description || ''}
                    onChange={(e) => updateSection('driver', 'description', e.target.value)}
                  />
                  <TextField
                    label={t('driver_primary_cta_label') || 'Primary CTA label'}
                    fullWidth
                    sx={{ mt: 2 }}
                    value={draft.driver?.primaryCtaLabel || ''}
                    onChange={(e) => updateSection('driver', 'primaryCtaLabel', e.target.value)}
                  />
                  <TextField
                    label={t('driver_primary_cta_link') || 'Primary CTA link'}
                    fullWidth
                    sx={{ mt: 2 }}
                    value={draft.driver?.primaryCtaLink || ''}
                    onChange={(e) => updateSection('driver', 'primaryCtaLink', e.target.value)}
                  />
                  <TextField
                    label={t('driver_secondary_cta_label') || 'Secondary CTA label'}
                    fullWidth
                    sx={{ mt: 2 }}
                    value={draft.driver?.secondaryCtaLabel || ''}
                    onChange={(e) => updateSection('driver', 'secondaryCtaLabel', e.target.value)}
                  />
                  <TextField
                    label={t('driver_secondary_cta_link') || 'Secondary CTA link'}
                    fullWidth
                    sx={{ mt: 2 }}
                    value={draft.driver?.secondaryCtaLink || ''}
                    onChange={(e) => updateSection('driver', 'secondaryCtaLink', e.target.value)}
                  />
                  <TextField
                    label={t('driver_image_label') || 'Driver image URL'}
                    fullWidth
                    sx={{ mt: 2 }}
                    value={draft.driver?.image || ''}
                    onChange={(e) => updateSection('driver', 'image', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontWeight: 800, mb: 1 }}>{t('business_promo_label') || 'Business promo'}</Typography>
                  <TextField
                    label={t('business_title_label') || 'Business title'}
                    fullWidth
                    value={draft.business?.title || ''}
                    onChange={(e) => updateSection('business', 'title', e.target.value)}
                  />
                  <TextField
                    label={t('business_description_label') || 'Business description'}
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mt: 2 }}
                    value={draft.business?.description || ''}
                    onChange={(e) => updateSection('business', 'description', e.target.value)}
                  />
                  <TextField
                    label={t('business_link_label') || 'Business link'}
                    fullWidth
                    sx={{ mt: 2 }}
                    value={draft.business?.link || ''}
                    onChange={(e) => updateSection('business', 'link', e.target.value)}
                  />
                  <TextField
                    label={t('business_link_text_label') || 'Business link text'}
                    fullWidth
                    sx={{ mt: 2 }}
                    value={draft.business?.linkText || ''}
                    onChange={(e) => updateSection('business', 'linkText', e.target.value)}
                  />
                  <Typography sx={{ fontWeight: 700, mt: 3, mb: 1 }}>{t('business_features_label') || 'Business features'}</Typography>
                  <MaterialTable
                    icons={TableIcons}
                    title={null}
                    columns={[
                      { title: t('title') || 'Title', field: 'title' },
                      { title: t('description') || 'Description', field: 'desc' },
                    ]}
                    data={draft.business?.features || []}
                    options={tableOptions}
                    editable={canUpdateSettings ? {
                      onRowAdd: (newData) => new Promise((resolve) => {
                        const next = [...(draft.business?.features || []), newData];
                        updateSection('business', 'features', next);
                        resolve();
                      }),
                      onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                        const next = (draft.business?.features || []).map((item) => item === oldData ? newData : item);
                        updateSection('business', 'features', next);
                        resolve();
                      }),
                      onRowDelete: (oldData) => new Promise((resolve) => {
                        const next = (draft.business?.features || []).filter((item) => item !== oldData);
                        updateSection('business', 'features', next);
                        resolve();
                      }),
                    } : {}}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.download}
            onChange={(e, value) => setExpanded((prev) => ({ ...prev, download: value }))}
            ref={(node) => { sectionRefs.current.download = node; }}
            id="download"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{t('download_section') || 'Download section'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={(
                  <Switch
                    checked={baseDraft.enabledSections?.download !== false}
                    disabled={!canEditGlobal}
                    onChange={(e) => updateGlobalSection('enabledSections', 'download', e.target.checked)}
                  />
                )}
                label={t('section_visible') || 'Section visible'}
              />
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('download_title_label') || 'Download title'}
                    fullWidth
                    value={draft.download?.title || ''}
                    onChange={(e) => updateSection('download', 'title', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('download_subtitle_label') || 'Download subtitle'}
                    fullWidth
                    value={draft.download?.subtitle || ''}
                    onChange={(e) => updateSection('download', 'subtitle', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {sectionToggle(t('show_apple_label') || 'Show Apple button', draft.download?.showApple !== false, (value) => updateSection('download', 'showApple', value))}
                    {sectionToggle(t('show_android_label') || 'Show Android button', draft.download?.showAndroid !== false, (value) => updateSection('download', 'showAndroid', value))}
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

            </Grid>
          </Grid>

        </CardContent>
      </Card>
    </Box>
  );
}
